import React, { useEffect, useRef, useState } from "react";
import { api, formatMoney, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Store, Pencil, Trash2, X, Save, ImagePlus, Package, Tag, FileSpreadsheet, Loader2 } from "lucide-react";

const EMPTY = { sku: "", name: "", price: "", quantity: 1, description: "" };

export default function AdminMarketplace() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [newFiles, setNewFiles] = useState([]); // [{file, preview}]
  const [existingImages, setExistingImages] = useState([]); // urls kept (edit mode)
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const load = () => {
    setLoading(true);
    api.get("/admin/marketplace").then((r) => setItems(r.data)).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const pickImages = (e) => {
    const files = Array.from(e.target.files || []);
    const accepted = [];
    for (const f of files) {
      if (!f.type.startsWith("image/")) { toast.error(`${f.name}: csak képfájl tölthető fel.`); continue; }
      if (f.size > 5 * 1024 * 1024) { toast.error(`${f.name}: túl nagy (max 5 MB).`); continue; }
      accepted.push({ file: f, preview: URL.createObjectURL(f) });
    }
    setNewFiles((arr) => [...arr, ...accepted]);
    if (fileRef.current) fileRef.current.value = "";
  };

  const resetForm = () => {
    setForm(EMPTY);
    setEditingId(null);
    setNewFiles([]);
    setExistingImages([]);
    if (fileRef.current) fileRef.current.value = "";
  };

  const startEdit = (it) => {
    setEditingId(it.id);
    setForm({
      sku: it.sku,
      name: it.name,
      price: it.base_price,
      quantity: it.stock ?? 1,
      description: it.description || "",
    });
    setNewFiles([]);
    setExistingImages(it.image_urls?.length ? it.image_urls : (it.image_url ? [it.image_url] : []));
    if (fileRef.current) fileRef.current.value = "";
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const save = async () => {
    if (!form.sku.trim() || !form.name.trim()) { toast.error("A cikkszám és a megnevezés kötelező."); return; }
    const price = Number(form.price);
    if (!price || price <= 0) { toast.error("Adj meg érvényes nettó árat (EUR)."); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("sku", form.sku.trim());
      fd.append("name", form.name.trim());
      fd.append("price", String(price));
      fd.append("quantity", String(Math.max(0, parseInt(form.quantity, 10) || 0)));
      fd.append("description", form.description || "");
      newFiles.forEach((nf) => fd.append("images", nf.file));
      if (editingId) {
        fd.append("existing_images", JSON.stringify(existingImages));
        await api.put(`/admin/marketplace/${editingId}`, fd);
        toast.success("Piactéri termék frissítve");
      } else {
        await api.post("/admin/marketplace", fd);
        toast.success("Termék feltöltve a Piactérre");
      }
      resetForm();
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (it) => {
    if (!window.confirm(`Biztosan törli a(z) ${it.sku} piactéri terméket?`)) return;
    try {
      await api.delete(`/admin/marketplace/${it.id}`);
      toast.success("Termék törölve a Piactérről");
      setItems((arr) => arr.filter((x) => x.id !== it.id));
      if (editingId === it.id) resetForm();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const toggleSold = async (it) => {
    try {
      const { data } = await api.patch(`/admin/marketplace/${it.id}/sold`, { sold: !it.sold });
      setItems((arr) => arr.map((x) => (x.id === it.id ? data : x)));
      toast.success(data.sold ? `${it.sku} → Eladva jelölve` : `${it.sku} → újra eladható`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const totalImages = existingImages.length + newFiles.length;

  return (
    <div>
      <h1 className="text-2xl font-bold mb-2 uppercase tracking-wide flex items-center gap-2">
        <Store size={22} /> Akciós Piactér
      </h1>
      <p className="text-sm mb-4" style={{ color: "var(--text-mute)" }}>
        Egyedi termékek feltöltése fix áron — a vevők a katalógus "Akciós Piactér" fülén és a keresőben találják meg őket.
      </p>
      <div className="border-b-2 mb-5" style={{ borderColor: "var(--brand)" }} />

      {/* XLSX bulk import — SNEFIX-style 4-column price list */}
      <BulkXlsxUploader onDone={load} />

      {/* Upload / edit form */}
      <div className="card-elev overflow-hidden mb-6" data-testid="marketplace-form">
        <div className="section-title">
          {editingId ? "Piactéri termék szerkesztése" : "Új termék feltöltése a Piactérre"}
        </div>
        <div className="p-4 bg-white grid md:grid-cols-[1fr_260px] gap-5">
          <div className="space-y-3">
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-bold uppercase block mb-1" style={{ color: "var(--text-mute)" }}>Cikkszám *</label>
                <input className="input" value={form.sku} onChange={(e) => setField("sku", e.target.value)}
                  placeholder="Pl. 06A115561B" data-testid="mp-sku" />
              </div>
              <div>
                <label className="text-xs font-bold uppercase block mb-1" style={{ color: "var(--text-mute)" }}>Megnevezés *</label>
                <input className="input" value={form.name} onChange={(e) => setField("name", e.target.value)}
                  placeholder="Pl. Olajszűrő készlet" data-testid="mp-name" />
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-bold uppercase block mb-1" style={{ color: "var(--text-mute)" }}>
                  Fix nettó ár (EUR) * <span className="normal-case font-normal">— minden vevő ezt látja</span>
                </label>
                <input className="input" type="number" min="0.01" step="0.01" value={form.price}
                  onChange={(e) => setField("price", e.target.value)} placeholder="Pl. 24.90" data-testid="mp-price" />
              </div>
              <div>
                <label className="text-xs font-bold uppercase block mb-1" style={{ color: "var(--text-mute)" }}>Darabszám (készlet)</label>
                <input className="input" type="number" min="0" value={form.quantity}
                  onChange={(e) => setField("quantity", e.target.value)} data-testid="mp-quantity" />
              </div>
            </div>
            <div>
              <label className="text-xs font-bold uppercase block mb-1" style={{ color: "var(--text-mute)" }}>Leírás (opcionális)</label>
              <textarea className="input" rows={3} value={form.description}
                onChange={(e) => setField("description", e.target.value)}
                placeholder="Állapot, kompatibilitás, egyéb infó..." data-testid="mp-description" />
            </div>
          </div>

          {/* Image gallery picker */}
          <div>
            <label className="text-xs font-bold uppercase block mb-1" style={{ color: "var(--text-mute)" }}>
              Fényképek {totalImages > 0 ? `(${totalImages})` : ""}
            </label>
            <div
              className="border-2 border-dashed rounded flex flex-col items-center justify-center text-center cursor-pointer p-3"
              style={{ borderColor: "var(--border-hi)", minHeight: 90, background: "#FAFAFA" }}
              onClick={() => fileRef.current?.click()}
              data-testid="mp-image-drop"
            >
              <ImagePlus size={24} style={{ color: "var(--text-disabled)" }} />
              <span className="text-xs mt-1" style={{ color: "var(--text-mute)" }}>
                Kattints képek kiválasztásához<br />(több is jelölhető — max 5 MB / kép)
              </span>
            </div>
            <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={pickImages} data-testid="mp-image-input" />

            {(existingImages.length > 0 || newFiles.length > 0) && (
              <div className="grid grid-cols-3 gap-2 mt-2" data-testid="mp-image-previews">
                {existingImages.map((url) => (
                  <div key={url} className="relative border" style={{ borderColor: "var(--border)" }}>
                    <img src={url} alt="" className="w-full h-16 object-cover" />
                    <button
                      className="absolute -top-1.5 -right-1.5 bg-red-600 text-white rounded-full w-4 h-4 flex items-center justify-center"
                      onClick={() => setExistingImages((arr) => arr.filter((u) => u !== url))}
                      title="Kép eltávolítása"
                      data-testid={`mp-img-remove-existing`}
                    >
                      <X size={10} />
                    </button>
                  </div>
                ))}
                {newFiles.map((nf, i) => (
                  <div key={i} className="relative border" style={{ borderColor: "var(--brand)" }}>
                    <img src={nf.preview} alt="" className="w-full h-16 object-cover" />
                    <button
                      className="absolute -top-1.5 -right-1.5 bg-red-600 text-white rounded-full w-4 h-4 flex items-center justify-center"
                      onClick={() => setNewFiles((arr) => arr.filter((_, j) => j !== i))}
                      title="Kép eltávolítása"
                      data-testid={`mp-img-remove-new`}
                    >
                      <X size={10} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="px-4 pb-4 bg-white flex items-center justify-end gap-2">
          {editingId && (
            <button onClick={resetForm} className="btn-ghost" disabled={saving} data-testid="mp-cancel">
              <X size={14} /> Mégse
            </button>
          )}
          <button onClick={save} className="btn-yellow" disabled={saving} data-testid="mp-save">
            <Save size={14} /> {saving ? "Mentés..." : editingId ? "Módosítások mentése" : "Feltöltés a Piactérre"}
          </button>
        </div>
      </div>

      {/* Item list */}
      <div className="card-elev overflow-hidden">
        <div className="section-title">Piactéri termékek ({items.length})</div>
        <div className="overflow-x-auto bg-white">
          <table className="table-b2b" data-testid="marketplace-table">
            <thead>
              <tr>
                <th style={{ width: 64 }}>Kép</th>
                <th style={{ width: 150 }}>Cikkszám</th>
                <th>Megnevezés</th>
                <th style={{ width: 110 }} className="text-right">Fix ár (nettó)</th>
                <th style={{ width: 70 }}>Készlet</th>
                <th style={{ width: 110 }}>Állapot</th>
                <th style={{ width: 140 }}>Feltöltve</th>
                <th style={{ width: 100 }}></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} className="text-center py-8" style={{ color: "var(--text-mute)" }}>Betöltés...</td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={8} className="text-center py-10">
                  <Package size={32} className="mx-auto mb-2" style={{ color: "var(--text-disabled)" }} />
                  <span className="text-sm" style={{ color: "var(--text-mute)" }}>Még nincs piactéri termék — töltsd fel az elsőt fent.</span>
                </td></tr>
              ) : (
                items.map((it) => {
                  const imgCount = it.image_urls?.length || (it.image_url ? 1 : 0);
                  return (
                    <tr key={it.id} data-testid={`mp-row-${it.sku}`} style={it.sold ? { opacity: 0.6 } : {}}>
                      <td>
                        <div className="w-12 h-12 flex items-center justify-center border relative" style={{ borderColor: "var(--border)" }}>
                          {it.image_url ? (
                            <img src={it.image_url} alt={it.name} className="w-full h-full object-cover" />
                          ) : (
                            <Store size={16} style={{ color: "var(--text-disabled)" }} />
                          )}
                          {imgCount > 1 && (
                            <span className="absolute bottom-0 right-0 bg-black/70 text-white text-[9px] px-1">{imgCount}</span>
                          )}
                        </div>
                      </td>
                      <td><span className="font-mono text-xs font-bold" style={{ color: "var(--brand)" }}>{it.sku}</span></td>
                      <td>
                        <div className="font-bold">{it.name}</div>
                        {it.description && <div className="text-xs truncate max-w-[320px]" style={{ color: "var(--text-mute)" }}>{it.description}</div>}
                      </td>
                      <td className="text-right font-mono font-bold">{formatMoney(it.base_price)}</td>
                      <td className="font-mono">{it.stock ?? 0}</td>
                      <td>
                        <button
                          onClick={() => toggleSold(it)}
                          className="text-[10px] uppercase font-bold px-2 py-1 rounded border"
                          style={it.sold
                            ? { background: "#FDECEC", color: "#c8102e", borderColor: "#c8102e" }
                            : { background: "#E8F8E8", color: "#0A8043", borderColor: "#0A8043" }}
                          title={it.sold ? "Kattints: újra eladhatóvá tétel" : "Kattints: Eladva jelölés"}
                          data-testid={`mp-sold-toggle-${it.sku}`}
                        >
                          <Tag size={10} className="inline mr-1" />{it.sold ? "Eladva" : "Eladható"}
                        </button>
                      </td>
                      <td className="font-mono text-xs" style={{ color: "var(--text-mute)" }}>
                        {it.created_at ? new Date(it.created_at).toLocaleString("hu-HU", { dateStyle: "short", timeStyle: "short" }) : "—"}
                      </td>
                      <td>
                        <div className="flex items-center gap-1">
                          <button onClick={() => startEdit(it)} className="btn-ghost !py-1 !px-2 text-xs" title="Szerkesztés" data-testid={`mp-edit-${it.sku}`}>
                            <Pencil size={12} />
                          </button>
                          <button onClick={() => remove(it)} className="btn-danger !py-1 !px-2 text-xs" title="Törlés" data-testid={`mp-delete-${it.sku}`}>
                            <Trash2 size={12} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}


// --------------------------------------------------------------------------
// XLSX bulk uploader for SNEFIX-style price lists:
//   Header: Cikkszám | Megnevezés | PCS | EUR   (aliases supported).
//   Every row inserted with base_price = EUR × (1 + margin/100).
//   HUF display rate is admin-tunable elsewhere (Tartalom → currency_huf_rate).
// --------------------------------------------------------------------------
function BulkXlsxUploader({ onDone }) {
  const [open, setOpen] = React.useState(false);
  const [margin, setMargin] = React.useState(35);
  const [replaceExisting, setReplaceExisting] = React.useState(false);
  const [uploading, setUploading] = React.useState(false);
  const [lastResult, setLastResult] = React.useState(null);
  const fileRef = React.useRef(null);

  const handle = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!/\.(xlsx|xlsm)$/i.test(file.name)) {
      toast.error("Csak XLSX (Excel) fájl támogatott.");
      if (fileRef.current) fileRef.current.value = "";
      return;
    }
    if (replaceExisting) {
      if (!window.confirm(
        "BIZTOSAN kitörlöd a JELENLEGI piactéri termékeket és feltöltöd a fájlból?\n\nEz a művelet nem vonható vissza (a törölt termékek elvesznek).",
      )) {
        if (fileRef.current) fileRef.current.value = "";
        return;
      }
    }
    setUploading(true);
    setLastResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("margin_percent", String(margin));
      fd.append("replace_existing", String(replaceExisting));
      const { data } = await api.post("/admin/marketplace/upload-xlsx", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 0,
      });
      setLastResult(data);
      const deletedMsg = data.deleted_before ? ` · ${data.deleted_before} régi törölve` : "";
      const currencyMsg = data.price_currency === "HUF"
        ? ` · Forintos ár (rate ${data.huf_rate_used}, árrés kihagyva)`
        : "";
      toast.success(
        `Piactér feltöltés kész: ${data.inserted} új · ${data.updated} frissítve${deletedMsg}${currencyMsg}` +
        (data.skipped_bad_row || data.skipped_zero_price
          ? ` (${data.skipped_bad_row + data.skipped_zero_price} sor kihagyva)`
          : ""),
      );
      onDone && onDone();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div className="card-elev mb-6 overflow-hidden" data-testid="marketplace-xlsx-upload">
      <div
        className="section-title cursor-pointer flex items-center justify-between"
        style={{ background: "linear-gradient(180deg, #0f766e 0%, #134e4a 100%)" }}
        onClick={() => setOpen((s) => !s)}
      >
        <span className="flex items-center gap-2">
          <FileSpreadsheet size={14} /> XLSX tömeges feltöltés a Piactérre
        </span>
        <span className="text-[10px] opacity-80">{open ? "▲" : "▼"}</span>
      </div>
      {open && (
        <div className="p-4 bg-white text-sm">
          <ul className="text-xs space-y-1 mb-3" style={{ color: "var(--text-mute)" }}>
            <li>&bull; Elvárt oszlopok: <strong>Cikkszám · Megnevezés · PCS · EUR</strong> (a header sor lehet bárhol az első 20 sorban).</li>
            <li>&bull; A megadott <strong>árrés (%)</strong> beleégetjük az árba: <code className="font-mono">base_price = EUR × (1 + árrés / 100)</code>.</li>
            <li>&bull; A vevő ezt az árat látja EUR-ban, HUF nézetben az admin által beállított <strong>currency_huf_rate</strong> szorzóval (jelenleg beállítható a Tartalom oldalon).</li>
            <li>&bull; A darabszám (PCS) a készletet állítja. Meglévő azonos cikkszámú piactéri termék felülírásra kerül (idempotens).</li>
          </ul>
          <div className="flex flex-wrap items-end gap-3 mb-3">
            <div>
              <label className="text-[10px] font-bold uppercase block mb-1" style={{ color: "var(--text-mute)" }}>Árrés %</label>
              <input
                type="number"
                min="0" max="500" step="0.5"
                className="input !py-1.5 !w-24 text-sm"
                value={margin}
                onChange={(e) => setMargin(Number(e.target.value))}
                disabled={uploading}
                data-testid="marketplace-xlsx-margin"
              />
            </div>
            <label
              className="flex items-center gap-2 text-xs cursor-pointer pb-1.5"
              style={{ color: replaceExisting ? "#c8102e" : "var(--text)" }}
              data-testid="marketplace-xlsx-replace-label"
            >
              <input
                type="checkbox"
                checked={replaceExisting}
                onChange={(e) => setReplaceExisting(e.target.checked)}
                disabled={uploading}
                data-testid="marketplace-xlsx-replace"
              />
              <span className="font-bold uppercase">
                Régi termékek CSERÉLÉSE {replaceExisting && "⚠"}
              </span>
              <span style={{ color: "var(--text-mute)" }}>
                (kitörli a jelenlegi piactéri termékeket, mielőtt feltölti)
              </span>
            </label>
            <label
              className="cursor-pointer flex-1 min-w-[220px] p-3 text-center transition-all flex items-center justify-center gap-2"
              style={{
                border: `2px dashed ${uploading ? "var(--brand)" : "#0f766e"}`,
                borderRadius: 4,
                background: uploading ? "#fff" : "rgba(15,118,110,0.05)",
                color: "#0f766e",
                fontWeight: 700,
                textTransform: "uppercase",
                fontSize: 12,
              }}
              data-testid="marketplace-xlsx-dropzone"
            >
              {uploading ? (
                <><Loader2 size={16} className="animate-spin" /> Feltöltés folyamatban...</>
              ) : (
                <><FileSpreadsheet size={16} /> Húzd ide vagy kattints az XLSX-hez</>
              )}
              <input
                ref={fileRef}
                type="file"
                accept=".xlsx,.xlsm"
                className="hidden"
                onChange={handle}
                disabled={uploading}
                data-testid="marketplace-xlsx-input"
              />
            </label>
          </div>
          {lastResult && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-center" data-testid="marketplace-xlsx-result">
              <div className="card-flat p-2">
                <div className="text-[10px] uppercase font-bold" style={{ color: "var(--text-mute)" }}>Új</div>
                <div className="text-xl font-bold" style={{ color: "#0A8043" }}>{lastResult.inserted}</div>
              </div>
              <div className="card-flat p-2">
                <div className="text-[10px] uppercase font-bold" style={{ color: "var(--text-mute)" }}>Frissítve</div>
                <div className="text-xl font-bold" style={{ color: "#2680C2" }}>{lastResult.updated}</div>
              </div>
              <div className="card-flat p-2">
                <div className="text-[10px] uppercase font-bold" style={{ color: "var(--text-mute)" }}>Hibás sor</div>
                <div className="text-xl font-bold" style={{ color: "#c8102e" }}>{lastResult.skipped_bad_row}</div>
              </div>
              <div className="card-flat p-2">
                <div className="text-[10px] uppercase font-bold" style={{ color: "var(--text-mute)" }}>0 ár / hiányzó</div>
                <div className="text-xl font-bold" style={{ color: "#c8102e" }}>{lastResult.skipped_zero_price}</div>
              </div>
              {(lastResult.skipped_examples || []).length > 0 && (
                <div className="col-span-full text-[11px] text-left p-2 rounded"
                     style={{ background: "#FDECEC", border: "1px solid #c8102e", color: "#c8102e" }}>
                  <strong>Kihagyott sorok:</strong>{" "}
                  {lastResult.skipped_examples.map((ex, i) => (
                    <span key={i} className="mr-2">
                      <code className="font-mono">{ex.sku}</code>: {ex.reason};
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
